import { z } from 'zod'
import {
  CapabilityEntry,
  CapabilityRegistry,
  type CapabilityApproval,
  type CapabilityExecutionMode,
} from './capabilities.js'
import { McpScope } from './mcp-scopes.js'
import { SCHEMA_VERSION } from './version.js'

/**
 * MCP capability registry declaration — SM2 (C3, C4, C7, D14; register §6).
 *
 * The registry *schema* is S0.11's (`./capabilities.ts`); this is the declaration SM2's host-policy
 * adapter projects. Two rules are load-bearing and both are structural rather than documentary:
 *
 * 1. **The registry declares, it never authorizes.** `execution_modes` says where a capability may
 *    run; it says nothing about whether *this* caller may run it. The server rechecks eligibility,
 *    grants, the authorization revision and approval at execution (C4, C7).
 * 2. **A family is derived, not authored.** Which scopes a capability needs decides whether it is a
 *    read, a write or a commerce capability, so a capability cannot be re-labelled into a weaker
 *    policy family by editing a field. `purchase.start` is what makes a capability commerce; a
 *    `*.write` scope is what makes it a write.
 *
 * `P17-10` (which field types may appear in AI-host content, and which hosts are excluded) is
 * **open** (register §10). No entry here names a field type or a field-type eligibility: the
 * declaration covers operations, never content, so the fail-closed branch is structural.
 */

/** The three policy families the adapter projects. Derived from `oauth_scopes`, never authored. */
export const MCP_CAPABILITY_FAMILIES = ['read', 'write', 'commerce'] as const
export const McpCapabilityFamily = z.enum(MCP_CAPABILITY_FAMILIES)
export type McpCapabilityFamily = z.infer<typeof McpCapabilityFamily>

/** The scope that makes a capability a commerce capability (B13, B21.4.4). */
export const COMMERCE_SCOPE: McpScope = 'purchase.start'

/**
 * A write capability is one whose operation is granted by a write scope. `delivery.write` and
 * `account.manage` are the two non-`*.write` scope names that still describe a consequential write.
 */
export function isWriteScope(scope: string): boolean {
  return scope.endsWith('.write') || scope === 'account.manage'
}

export function capabilityFamilyOf(
  entry: Pick<CapabilityEntry, 'oauth_scopes'>,
): McpCapabilityFamily {
  if (entry.oauth_scopes.includes(COMMERCE_SCOPE)) return 'commerce'
  if (entry.oauth_scopes.some((scope) => isWriteScope(scope))) return 'write'
  return 'read'
}

/** The C4 field defaults the MCP declaration shares; an entry overrides only what differs. */
const PLATFORMS = ['web-desktop-chromium', 'web-desktop-firefox', 'web-ios-safari', 'native-ios']
const HOST_SURFACES = ['mcp', 'web-app']
const POLICY_VERSION = 1

interface CapabilityDeclaration {
  readonly id: string
  readonly requirements: readonly string[]
  readonly scopes: readonly McpScope[]
  readonly modes?: readonly CapabilityExecutionMode[]
  /** Tool names this capability backs; the adapter maps a tool to its capability through this. */
  readonly tools: readonly string[]
  readonly approval?: CapabilityApproval
  readonly selected_object?: boolean
  readonly lifetime_only?: boolean
  readonly effects?: CapabilityEntry['effects']
  readonly preconditions: readonly string[]
  readonly resulting_state: string
  readonly prohibited: readonly string[]
  readonly recovery: string
  readonly tests: readonly string[]
}

function declare(input: CapabilityDeclaration): CapabilityEntry {
  return CapabilityEntry.parse({
    capability_id: input.id,
    requirement_ids: [...input.requirements],
    eligibility: { free: true, lifetime: true },
    effects: input.effects ?? { trial: 'NONE', credit: 'NONE', active_request: 'NONE' },
    execution_modes: [...(input.modes ?? ['REMOTE'])],
    platforms: PLATFORMS,
    oauth_scopes: [...input.scopes],
    selected_object_permission_required: input.selected_object ?? false,
    approval: input.approval ?? 'NONE',
    preconditions: [...input.preconditions],
    resulting_state: input.resulting_state,
    prohibited_side_effects: [...input.prohibited],
    tool_schema_ids: [...input.tools],
    host_surfaces: HOST_SURFACES,
    policy_version: POLICY_VERSION,
    recovery_behaviour: input.recovery,
    test_ids: [...input.tests],
  })
}

/**
 * The read capabilities SM2 serves, plus the write and commerce capabilities the adapter must be
 * able to *withhold*. A commerce capability declares `REMOTE` because the server can honour it in
 * the application; the adapter decides whether a given registered host may see it at all.
 */
export const MCP_CAPABILITY_ENTRIES: readonly CapabilityEntry[] = [
  declare({
    id: 'mcp.account.read',
    requirements: ['B7.2', 'B8.1'],
    scopes: ['profile.read'],
    tools: ['signbento_get_account'],
    preconditions: ['an ACTIVE host grant holding profile.read'],
    resulting_state: 'the account profile and plan state are returned',
    prohibited: [
      'returning another account profile',
      'returning billing fields without billing.read',
    ],
    recovery: 'without profile.read the call is refused with SCOPE_REQUIRED and nothing is read',
    tests: ['MCP-AC-004'],
  }),
  declare({
    id: 'mcp.agreements.list',
    requirements: ['B7.2', 'B18.1'],
    scopes: ['agreements.read'],
    tools: ['signbento_list_envelopes'],
    preconditions: ['an ACTIVE host grant holding agreements.read'],
    resulting_state: 'a page of host-granted agreement metadata is returned',
    prohibited: ['counting or paging rows outside the grant', 'returning local-only items'],
    recovery: 'an unauthorized call is refused before the query runs',
    tests: ['MCP-AC-034'],
  }),
  declare({
    id: 'mcp.agreements.read',
    requirements: ['B7.2'],
    scopes: ['agreements.read'],
    tools: ['signbento_get_envelope'],
    selected_object: true,
    preconditions: ['the agreement is inside the grant item scope'],
    resulting_state: 'one agreement summary is returned by reference',
    prohibited: ['returning a signer capability link', 'inventing a delivery or view state'],
    recovery: 'an item outside the item scope answers exactly like an unknown id',
    tests: ['MCP-AC-034', 'MCP-AC-050'],
  }),
  declare({
    id: 'mcp.agreements.attention.read',
    requirements: ['B7.2', 'MCP-AC-051'],
    scopes: ['agreements.read'],
    tools: ['signbento_get_attention'],
    preconditions: ['an ACTIVE host grant holding agreements.read'],
    resulting_state: 'remote items needing attention are summarised',
    prohibited: ['including local-only drafts', 'inventing a pending state'],
    recovery: 'the read is scoped to the authorized account and remote rows only',
    tests: ['MCP-AC-051'],
  }),
  declare({
    id: 'mcp.final_artifacts.read',
    requirements: ['B7.2', 'B14.2'],
    scopes: ['evidence.read'],
    tools: ['signbento_get_final_artifacts'],
    selected_object: true,
    preconditions: ['the agreement is inside the grant item scope and finalization completed'],
    resulting_state: 'final artifact references are returned under their retention class',
    prohibited: ['returning document bytes', 'extending retention'],
    recovery: 'an unavailable artifact is reported as such rather than fabricated',
    tests: ['MCP-AC-034'],
  }),
  /*
   * The remaining read families — SM5 (B11.1, B14.2, B18.1, B7.2; MCP-AC-130, MCP-AC-119).
   *
   * Read families whose whole safety argument is what they *cannot* return. `P17-10` is open, so no
   * entry here may expose a field value or a field-type eligibility: the field-data entry returns
   * accepted values as references with their provenance, the audit entry returns the chain as
   * references under a named retention/redaction class, and the job entry returns bounded state
   * rather than a stream. Each entry states its bound as a precondition, because a read that can be
   * asked for an unbounded answer is not the capability this register declares.
   */
  declare({
    id: 'mcp.analytics.read',
    requirements: ['B7.3', '§9', 'C11'],
    scopes: ['analytics.read'],
    tools: ['signbento_get_analytics'],
    selected_object: false,
    preconditions: [
      'an ACTIVE host grant holding analytics.read',
      'the activation cohort is restricted to the host item grant before computation',
    ],
    resulting_state:
      'R10-R13 return the canonical connected metric envelopes and label selected-item cohorts explicitly',
    prohibited: [
      'including local-only documents',
      'labeling a selected-item cohort as a whole-account total',
      'treating provider acceptance as verified delivery',
    ],
    recovery: 'a metric without enough observations is null with its completeness reason',
    tests: ['MCP-AC-123', 'C11'],
  }),
  declare({
    id: 'mcp.field_data.read',
    requirements: ['B11.1', 'B7.2'],
    scopes: ['evidence.read'],
    tools: ['signbento_get_field_data'],
    selected_object: true,
    preconditions: [
      'an ACTIVE host grant holding evidence.read',
      'the agreement is inside the grant item scope',
      'a page of at most the declared bound is asked for',
    ],
    resulting_state:
      'the accepted datum set is returned as references with participant, field, revision, instant and evidence reference',
    prohibited: [
      'returning a field value, a document byte or an attachment name',
      'returning a datum for a participant or field outside the grant',
      'reading the object a reference names',
    ],
    recovery:
      'a participant, field or agreement outside the grant is absent, exactly like an unknown id (OP_NOT_FOUND); a field value is withheld by P17-10 until the register decision is recorded',
    tests: ['MCP-AC-130'],
  }),
  declare({
    id: 'mcp.audit_events.read',
    requirements: ['B11.1', 'B7.2', '§7.4'],
    scopes: ['evidence.read'],
    tools: ['signbento_get_audit_events'],
    selected_object: true,
    preconditions: [
      'an ACTIVE host grant holding evidence.read',
      'the agreement is inside the grant item scope',
      'a window no wider than the declared bound and a page no larger than the declared bound',
    ],
    resulting_state:
      'canonical events in chain order, each with its retention and redaction class and its chain link',
    prohibited: [
      'returning an unbounded or unwindowed chain',
      'returning a payload value that is not a reference',
      'inventing an event, an instant or a chain digest',
    ],
    recovery:
      'a payload value that is not a reference is withheld by name in the event’s withheld_keys rather than blanked; a wider window or a larger page is refused before the read',
    tests: ['MCP-AC-130'],
  }),
  declare({
    id: 'mcp.jobs.read',
    requirements: ['B7.2', 'B15.3', 'MCP-AC-119'],
    scopes: ['profile.read'],
    tools: ['signbento_get_job'],
    // The scope split is the branch, not the tool: the tool reads account-level job status, and
    // each family's own object scope is required by the branch that reads it (the same conditional
    // challenge SM5's managed delivery branch uses).
    preconditions: [
      'an ACTIVE host grant holding profile.read, and the family’s own scope for the branch',
      'a job handle this service minted, or the three facts a handle is made of',
    ],
    resulting_state:
      'one job’s bounded state, its outcome and its poll bound are reported, or an honest UNKNOWN',
    prohibited: [
      'reporting a state no server row proves',
      'reporting another account’s job',
      'claiming a terminal outcome the service has not recorded',
      'answering for a batch job, which has no producer in this build',
    ],
    recovery:
      'a handle outside the grant answers like an unknown id (OP_NOT_FOUND); a missing branch scope is SCOPE_REQUIRED with the scope named; a family with no producer is refused rather than guessed',
    tests: ['MCP-AC-119'],
  }),
  declare({
    id: 'mcp.connection.activity.read',
    requirements: ['B8', 'B21.6'],
    scopes: ['profile.read'],
    tools: ['signbento_get_connection_activity'],
    preconditions: ['an ACTIVE host grant holding profile.read'],
    resulting_state: 'grant and usage counts for this account are returned',
    prohibited: ['returning another account grant', 'returning tool arguments or content'],
    recovery: 'the read is scoped to the authorized account',
    tests: ['MCP-AC-008'],
  }),
  declare({
    id: 'mcp.capability.discovery',
    requirements: ['C7', 'B7.2'],
    scopes: ['profile.read'],
    tools: ['signbento_describe_capabilities'],
    preconditions: ['an ACTIVE host grant holding profile.read'],
    resulting_state: 'eligible actions, modes, limits and blockers are reported',
    prohibited: ['exposing an ungranted object', 'treating discovery as authorization'],
    recovery:
      'discovery is rechecked at execution against eligibility, grants, revision and approval',
    tests: ['MCP-AC-004'],
  }),
  declare({
    id: 'mcp.agreements.write',
    requirements: ['C3', 'B3.3'],
    scopes: ['agreements.write'],
    // The server can prepare remotely, and a person can complete it in the browser. The adapter
    // removes REMOTE for every host — writes are never performed in-host — and the handoff stays.
    modes: ['REMOTE', 'HUMAN_HANDOFF'],
    tools: [
      'signbento_prepare_envelope',
      'signbento_get_review_status',
      'signbento_commit_envelope',
      'signbento_get_editor_link',
    ],
    approval: 'HUMAN_APPROVAL',
    preconditions: ['an ACTIVE host grant holding agreements.write', 'a fresh human approval'],
    resulting_state: 'the agreement is prepared and awaits the person in a secure browser context',
    prohibited: ['writing in-host', 'committing without approval'],
    recovery: 'a host that cannot write in place returns a secure browser continuation',
    tests: ['MCP-AC-125'],
  }),
  declare({
    id: 'mcp.agreements.manual_share',
    requirements: ['B10.1', 'B7.5'],
    scopes: ['agreements.write'],
    // The manual lane: the server derives the capability link (remote), and the *person* transmits
    // it through whatever channel they choose (human handoff). There is no in-host execution to
    // remove — a share is never sent by SignBento on this lane — and the adapter therefore keeps both
    // declared modes rather than narrowing one away.
    modes: ['REMOTE', 'HUMAN_HANDOFF'],
    tools: ['signbento_commit_manual_share', 'signbento_get_signing_link'],
    approval: 'HUMAN_APPROVAL',
    preconditions: [
      'an ACTIVE host grant holding agreements.write',
      'an action a person approved and committed, completed by the person in the browser',
    ],
    resulting_state:
      'the share package is issued to the sender, who transmits it themselves at zero delivery credit',
    prohibited: [
      'sending the message from SignBento',
      'claiming a transport delivery or a link open',
      'returning a link for an envelope outside the grant or for a participant that was not approved',
      'charging a delivery credit for a manual share',
    ],
    recovery:
      'an envelope the person has not completed is refused by name, and a managed envelope names that there is no manual link',
    tests: ['MCP-AC-044', 'MCP-AC-045', 'MCP-AC-046'],
  }),
  /*
   * The operational families — SM5 (B7.5, B7.3; AT-015/016, MCP-AC-128).
   *
   * Four capabilities, each a prepare/commit pair on SM3's approval model: a model prepares the
   * edit and states its credit effect, a person approves it in the trusted surface, and the commit
   * re-validates the live state before it runs. `REMOTE` is declared because the *service* performs
   * the edit (a correction rotates a capability, a resend queues an invitation, a void revokes,
   * an extension lengthens) — but only after a recorded human approval, which is why
   * `HUMAN_HANDOFF` is declared beside it and why the adapter's write projection leaves the tool
   * reachable through that handoff mode rather than withholding the capability.
   *
   * Every entry states the same two prohibitions, because both would be easy to get wrong: an
   * operational edit never touches accepted evidence, and it never costs another delivery credit
   * (a reminder is not a second delivery — AT-015, MCP-AC-025).
   */
  declare({
    id: 'mcp.agreements.correction',
    requirements: ['B7.5', 'B7.3', 'AT-015'],
    scopes: ['agreements.write'],
    modes: ['REMOTE', 'HUMAN_HANDOFF'],
    tools: ['signbento_prepare_correction', 'signbento_commit_correction'],
    approval: 'HUMAN_APPROVAL',
    selected_object: true,
    preconditions: [
      'an ACTIVE host grant holding agreements.write, and delivery.write when the correction also resends a managed invitation',
      'a fresh human approval',
      'a Lifetime account, an ACTIVE unexpired agreement and a participant that has not submitted',
    ],
    resulting_state:
      'the participant’s identity is replaced and its capability rotated, with accepted evidence untouched',
    prohibited: [
      'editing accepted evidence or consent',
      'resetting the resend budget',
      'changing order, kind or the sender flag',
      'charging a delivery credit',
    ],
    recovery:
      'the refusal is the code the application path answers with (CORRECTION_REQUIRES_LIFETIME, RECIPIENT_ALREADY_SUBMITTED, ENVELOPE_TERMINAL, LINK_EXPIRED, OP_REVISION_MISMATCH)',
    tests: ['MCP-AC-047', 'MCP-AC-049'],
  }),
  declare({
    id: 'mcp.agreements.resend',
    requirements: ['B7.5', 'B7.3', 'AT-015'],
    scopes: ['agreements.write'],
    modes: ['REMOTE', 'HUMAN_HANDOFF'],
    tools: ['signbento_prepare_resend', 'signbento_commit_resend'],
    approval: 'HUMAN_APPROVAL',
    selected_object: true,
    preconditions: [
      'an ACTIVE host grant holding agreements.write, and delivery.write when the agreement is managed',
      'a fresh human approval',
      'an ACTIVE unexpired agreement, a signer that has not submitted, and resend budget left for the slot',
    ],
    resulting_state:
      'a second invitation for the same slot: queued through the managed path, or the existing link revealed on the manual branch',
    prohibited: [
      'queuing a second invitation for a slot whose budget is spent',
      'charging a delivery credit',
      'claiming a transport delivery or a link open',
    ],
    recovery:
      'the refusal is the code the application path answers with (RESEND_LIMIT_REACHED, RESEND_TOO_SOON, RECIPIENT_ALREADY_SUBMITTED, ENVELOPE_TERMINAL, LINK_EXPIRED)',
    tests: ['MCP-AC-025', 'MCP-AC-048', 'MCP-AC-049'],
  }),
  declare({
    id: 'mcp.agreements.void',
    requirements: ['B7.5', 'B7.3'],
    scopes: ['agreements.write'],
    modes: ['REMOTE', 'HUMAN_HANDOFF'],
    tools: ['signbento_prepare_void', 'signbento_commit_void'],
    approval: 'HUMAN_APPROVAL',
    selected_object: true,
    preconditions: [
      'an ACTIVE host grant holding agreements.write',
      'a fresh human approval',
      'an ACTIVE agreement',
    ],
    resulting_state:
      'the agreement is VOIDED, every outstanding capability is revoked and queued invitations are skipped',
    prohibited: [
      'voiding an agreement that is already terminal',
      'reopening a voided agreement',
      'charging a delivery credit',
    ],
    recovery:
      'the refusal is ENVELOPE_TERMINAL, or OP_REVISION_MISMATCH when the revision the approval quoted moved',
    tests: ['MCP-AC-047', 'MCP-AC-049'],
  }),
  declare({
    id: 'mcp.agreements.expiry_extension',
    requirements: ['B7.5', 'B7.3', 'AT-016'],
    scopes: ['agreements.write'],
    modes: ['REMOTE', 'HUMAN_HANDOFF'],
    tools: ['signbento_prepare_expiry_extension', 'signbento_commit_expiry_extension'],
    approval: 'HUMAN_APPROVAL',
    selected_object: true,
    lifetime_only: true,
    preconditions: [
      'an ACTIVE host grant holding agreements.write',
      'a fresh human approval',
      'a Lifetime account, an ACTIVE unexpired agreement, zero accepted responses and a longer deadline within the register ceiling',
    ],
    resulting_state:
      'the signing deadline is longer, measured from the original activation, with the contract untouched',
    prohibited: [
      'shortening a deadline',
      'extending after an accepted response',
      'reopening an expired request',
      'charging a delivery credit',
    ],
    recovery:
      'the refusal is the code the application path answers with (EXTENSION_REQUIRES_LIFETIME, RESPONSES_ACCEPTED, EXTENSION_NOT_LONGER, PLAN_LIMIT with limitation.signing_days, LINK_EXPIRED)',
    tests: ['MCP-AC-128', 'MCP-AC-047'],
  }),
  declare({
    id: 'mcp.handoff.source',
    requirements: ['C5', 'B7.3'],
    scopes: ['workspace.write'],
    // A handoff is never remote execution and never in-host: it is the person acting on their own
    // device, which is exactly the two handoff modes. `REMOTE` is not declared, so no adapter has
    // to remove it — the mode the capability can never have is not expressible in the first place.
    modes: ['DEVICE_HANDOFF', 'HUMAN_HANDOFF'],
    tools: [
      'signbento_begin_document_handoff',
      'signbento_get_handoff_status',
      'signbento_cancel_handoff',
    ],
    // The person's own action in the app is the authority for a handoff; nothing is activated,
    // nothing is published and no content returns to the host without a separate authorization.
    approval: 'NONE',
    preconditions: ['an ACTIVE host grant holding workspace.write'],
    resulting_state: 'an opaque operation is open and waits for the person on their own device',
    prohibited: [
      'activating an envelope',
      'listing the device inventory to the host',
      'promising background execution while the app is suspended or closed',
      'returning selected content to the host without a separate authorization',
    ],
    recovery:
      'an unavailable source or a wrong device is reported honestly, with a restart offered',
    tests: ['MCP-AC-031', 'MCP-AC-032', 'MCP-AC-033', 'MCP-AC-035'],
  }),
  /*
   * The billing-recognition family — SM6 (B13, B13.1; MCP-AC-024..027, 060, PUR-15 analog).
   *
   * Two reads whose whole point is that an existing entitlement works on *every* host: neither is
   * commerce, so no adapter withholds either, and the OpenAI published adapter offers them exactly
   * as the app does. `billing.read` is the scope because both operations read the account's own
   * verified purchases and derived entitlement; neither starts a purchase, and neither can grant
   * one (`grants_new_entitlement: false` is a literal in the restore answer).
   */
  declare({
    id: 'mcp.billing.purchases.read',
    requirements: ['B13', 'B13.1'],
    scopes: ['billing.read'],
    tools: ['signbento_get_purchases'],
    preconditions: ['an ACTIVE host grant holding billing.read'],
    resulting_state:
      'the account’s verified purchases and its derived entitlement are returned, metadata only',
    prohibited: [
      'returning card data, a provider payload or a provider transaction locator',
      'returning another account’s purchase',
      'offering a repurchase to an account that already holds Lifetime',
    ],
    recovery:
      'without billing.read the call is refused with SCOPE_REQUIRED and nothing is read; a withheld commerce posture is reported as a prompt state, never as a link',
    tests: ['MCP-AC-026'],
  }),
  declare({
    id: 'mcp.billing.restore',
    requirements: ['B13', 'B13.1', 'PUR-04'],
    scopes: ['billing.read'],
    tools: ['signbento_restore_purchases'],
    preconditions: ['an ACTIVE host grant holding billing.read'],
    resulting_state:
      'the entitlement is re-derived from the service’s own verified records and the S6.3 reconciliation is reported',
    prohibited: [
      'granting an entitlement the provider did not verify',
      'granting a second bundle for a purchase already applied',
      'implying that a local document is restored',
    ],
    recovery:
      'the answer is the endpoint’s own shape: `provider_checked: false` when no provider adapter is wired, and the three literals that say a restore grants nothing and restores no local document',
    tests: ['MCP-AC-060'],
  }),
  declare({
    id: 'mcp.commerce.checkout',
    requirements: ['B13', 'B21.4.4'],
    scopes: [COMMERCE_SCOPE],
    tools: ['signbento_start_checkout'],
    preconditions: ['a registered adapter whose directory policy permits hosted initiation'],
    resulting_state: 'a provider-hosted checkout session is created for the account',
    prohibited: [
      'returning card data or a payment credential surface other than the provider’s own page',
      'exposing a checkout link from a restricted adapter',
      'creating a second payable session for a repeated start',
      'granting an entitlement from a redirect rather than from provider verification',
    ],
    recovery:
      'a withheld adapter gives a neutral limitation and an in-entitlement alternative, and never a URL',
    tests: ['MCP-AC-059', 'MCP-AC-060', 'MCP-AC-061', 'MCP-AC-062', 'MCP-AC-124'],
  }),
  declare({
    id: 'mcp.commerce.credit_pack',
    requirements: ['B13', 'B21.4.4'],
    scopes: [COMMERCE_SCOPE],
    // No tool of its own: a pack is a `product_id` on the checkout tool. The register now enables
    // three candidates at the owner's direction; the separate margin review remains open, while
    // checkout still requires an active Lifetime entitlement and a permitting host adapter.
    tools: [],
    preconditions: [
      'a registered adapter whose directory policy permits credit-pack purchase',
      'an enabled pack row in the config register and a verified Lifetime entitlement',
    ],
    resulting_state: 'a credit-pack purchase session is created for the account',
    prohibited: [
      'exposing a credit-pack link from a restricted adapter',
      'auto-recharge',
      'quoting a price or a margin for a pack whose validation is unrecorded',
    ],
    recovery:
      'a withheld adapter gives a neutral limitation and an in-entitlement alternative; an unvalidated pack is refused by the register gate with no session and no price',
    tests: ['MCP-AC-062', 'MCP-AC-124'],
  }),
  declare({
    id: 'mcp.commerce.upgrade',
    requirements: ['B12', 'B21.4.4'],
    scopes: [COMMERCE_SCOPE],
    // Also tool-less: an upgrade is not an action a host performs, it is what a *refusal* may offer
    // once where the adapter permits it (the B12 upgrade trigger).
    tools: [],
    preconditions: [
      'a registered adapter whose directory policy permits paid upsell',
      'a plan-dependent action the account’s current plan refused',
    ],
    resulting_state: 'an upgrade initiation is offered on a permitted surface',
    prohibited: [
      'freemium paid upsell on a restricted adapter',
      'routing to a purchase page',
      'repeating the offer inside one flow',
    ],
    recovery:
      'a withheld adapter preserves the draft and names the exact blocker and an in-entitlement alternative instead',
    tests: ['MCP-AC-028', 'MCP-AC-029', 'MCP-AC-030', 'MCP-AC-124'],
  }),
  declare({
    id: 'mcp.account.manage',
    requirements: ['B7.5', 'B8', '§13.5'],
    scopes: ['account.manage'],
    // The server can prepare the action; only the person can complete it. The adapter removes
    // `REMOTE` for every host (a write is never performed in-host), so the handoff is the whole of
    // what a host can reach — which is exactly MCP-AC-074's prepare-only path.
    modes: ['REMOTE', 'HUMAN_HANDOFF'],
    tools: ['signbento_prepare_account_action'],
    approval: 'HUMAN_APPROVAL',
    preconditions: [
      'an ACTIVE host grant holding account.manage',
      'for a host action, a grant that belongs to this account',
      'for a grant change, every item id naming an object this account owns',
    ],
    resulting_state:
      'the action is frozen and the person is handed the secure continuation that completes it',
    prohibited: [
      'performing a disconnect, a grant change, an export or a deletion from the model path',
      'completing a deletion without reauthentication',
      'reporting a revocation, an export or a deletion this service did not perform',
      'handing a host send, sign or delete authority in a continuation',
    ],
    recovery:
      'the continuation is the person’s own session at their own surface; a foreign grant or a foreign host answers like an id that never existed, and a deletion always reauthenticates',
    tests: ['MCP-AC-074'],
  }),
  declare({
    id: 'mcp.batch',
    requirements: ['B7.5', 'B7.3', 'B18.1'],
    scopes: ['agreements.read', 'agreements.write'],
    // The prepare freezes the set remotely and the commit is authorized item by item by the person;
    // the adapter removes `REMOTE` for every host, so the handoff mode is what survives — and the
    // batch is still reachable because the person’s approval is what performs anything.
    modes: ['REMOTE', 'HUMAN_HANDOFF'],
    tools: ['signbento_prepare_batch', 'signbento_commit_batch'],
    approval: 'HUMAN_APPROVAL',
    selected_object: true,
    preconditions: [
      'an ACTIVE host grant holding agreements.read, and agreements.write for a mutating operation',
      'one operation kind and at most 25 explicit item ids with the revision the caller read',
      'every item inside the grant item scope',
      'a person-recorded approval on each item’s own review',
    ],
    resulting_state:
      'the frozen item set is stored with its digest, and each approved item is performed once',
    prohibited: [
      'an unattended bulk creation or send',
      'inserting an item the person did not approve into an approved set',
      'repeating a completed item on a retry',
      'acting on an item whose revision moved',
    ],
    recovery:
      'a moved revision refuses that item alone with REVISION_CONFLICT; a retry re-attempts only the items that are not SUCCEEDED, and a changed item set answers REVISION_CONFLICT rather than swapping the approved set',
    tests: ['MCP-AC-122'],
  }),
]

/** The declaration as the versioned registry object, validated once at load. */
export const MCP_CAPABILITY_REGISTRY: CapabilityRegistry = CapabilityRegistry.parse({
  schema_version: SCHEMA_VERSION,
  registry_version: 1,
  entries: MCP_CAPABILITY_ENTRIES,
})

/**
 * Tool name → capability. A tool the registry does not declare maps to nothing, and a capability
 * the adapter withholds has no entry here for that adapter's catalogue — the tool is simply absent.
 */
export const CAPABILITY_BY_TOOL: Readonly<Record<string, CapabilityEntry>> = Object.fromEntries(
  MCP_CAPABILITY_ENTRIES.flatMap((entry) =>
    entry.tool_schema_ids.map((tool) => [tool, entry] as const),
  ),
)
